/**
 * EmoteWheel — the owner-tuned radial co-op comms wheel (TS/DOM port of the
 * prototype `docs/design/flow/ui.jsx:399-453`). Press-and-hold the 😄 button to
 * fan a semicircle of callouts across the top, drag to the nearest one, release
 * to fire it (release inside the dead-zone or with no hovered option = cancel).
 *
 * Scene-agnostic and wire-agnostic: it never imports Colyseus and never sends.
 * It calls `onPick(callout)` with the prototype's plain display string
 * `"<icon> <label>!"` (e.g. `"⤴️ Jump!"`), which the host maps directly onto the
 * existing free-form `EmotePayload.emote` (`ClientMessage.Emote`) — NO new wire
 * surface, NO shared/emote-id change (touch-controls-protocol §5).
 */

/** A single co-op callout (prototype `EMOTES`, `ui.jsx:400-406`). */
export interface EmoteOption {
  icon: string;
  label: string;
}

/** The 5 co-op callouts (NOT the old social face-emojis), `ui.jsx:400-406`. */
export const EMOTE_CALLOUTS: readonly EmoteOption[] = [
  { icon: '⤴️', label: 'Jump' },
  { icon: '🏃', label: 'Run' },
  { icon: '✋', label: 'Stop' },
  { icon: '👋', label: 'Come' },
  { icon: '🙏', label: 'Thanks' },
];

/** The wire string for a callout — the prototype's ``${icon} ${label}!`` form. */
export function calloutString(o: EmoteOption): string {
  return `${o.icon} ${o.label}!`;
}

export interface EmoteWheelOptions {
  /** Fired on release over a hovered callout, with `"<icon> <label>!"`. */
  onPick: (callout: string) => void;
  /** Callouts to fan; defaults to the 5 co-op callouts. */
  options?: readonly EmoteOption[];
  /** The 😄 trigger button diameter in px (prototype `size=52`). */
  size?: number;
}

export interface EmoteWheelHandle {
  /** The relatively-positioned root the host places in its comms cluster. */
  el: HTMLDivElement;
  destroy(): void;
}

// Prototype geometry (`ui.jsx:414`): fan radius + central dead-zone.
const R = 92;
const DEAD = 26;

/**
 * Mount an EmoteWheel. The returned `el` is a `position:relative` wrapper sized
 * `size x size`; the host positions it (e.g. bottom-right comms cluster). The
 * fan + dimmed radial backdrop overlay only exist while held.
 */
export function mountEmoteWheel(opts: EmoteWheelOptions): EmoteWheelHandle {
  const options = opts.options ?? EMOTE_CALLOUTS;
  const size = opts.size ?? 52;
  const n = options.length;

  // Fan the options across the TOP semicircle: 180°(left) → 360°(right),
  // through 270°(up). Identical to the prototype `angleOf`/`posOf`.
  const angleOf = (i: number): number =>
    ((180 + (n === 1 ? 90 : i * (180 / (n - 1)))) * Math.PI) / 180;
  const posOf = (i: number): { x: number; y: number } => ({
    x: Math.cos(angleOf(i)) * R,
    y: Math.sin(angleOf(i)) * R,
  });

  const root = document.createElement('div');
  root.className = 'ew-root';
  root.style.width = `${size}px`;
  root.style.height = `${size}px`;

  // The fan layer (hidden until held), anchored at the button centre.
  const fan = document.createElement('div');
  fan.className = 'ew-fan';
  fan.style.left = `${size / 2}px`;
  fan.style.top = `${size / 2}px`;

  const backdrop = document.createElement('div');
  backdrop.className = 'ew-backdrop';
  const backOff = R + 34;
  backdrop.style.left = `${-backOff}px`;
  backdrop.style.top = `${-backOff}px`;
  backdrop.style.width = `${backOff * 2}px`;
  backdrop.style.height = `${backOff * 2}px`;
  fan.appendChild(backdrop);

  const chips: HTMLDivElement[] = options.map((o, i) => {
    const p = posOf(i);
    const chip = document.createElement('div');
    chip.className = 'ew-chip';
    chip.style.left = `${p.x}px`;
    chip.style.top = `${p.y}px`;
    chip.innerHTML =
      `<span class="ew-icon">${o.icon}</span>` +
      `<span class="ew-label">${o.label}</span>`;
    fan.appendChild(chip);
    return chip;
  });

  const btn = document.createElement('button');
  btn.className = 'btn ghost ew-btn';
  btn.setAttribute('aria-label', 'emote');
  btn.style.width = `${size}px`;
  btn.style.height = `${size}px`;
  btn.textContent = '😄';

  root.append(fan, btn);

  let open = false;
  let hover = -1;
  const center = { x: 0, y: 0 };

  const setHover = (i: number): void => {
    if (i === hover) return;
    hover = i;
    chips.forEach((c, j) => c.classList.toggle('on', j === i));
  };

  const start = (e: PointerEvent): void => {
    const r = btn.getBoundingClientRect();
    center.x = r.left + r.width / 2;
    center.y = r.top + r.height / 2;
    open = true;
    setHover(-1);
    root.classList.add('ew-open');
    try {
      btn.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw on some ids; safe to ignore */
    }
    e.preventDefault();
  };

  const move = (e: PointerEvent): void => {
    if (!open) return;
    const dx = e.clientX - center.x;
    const dy = e.clientY - center.y;
    if (Math.hypot(dx, dy) < DEAD) {
      setHover(-1);
      return;
    }
    // Nearest option by angular distance (prototype `ui.jsx:425-427`).
    const ang = Math.atan2(dy, dx);
    let best = -1;
    let bd = 99;
    for (let i = 0; i < n; i++) {
      const d = Math.abs(
        Math.atan2(Math.sin(ang - angleOf(i)), Math.cos(ang - angleOf(i))),
      );
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    setHover(best);
  };

  const end = (): void => {
    if (open && hover >= 0) opts.onPick(calloutString(options[hover]!));
    open = false;
    setHover(-1);
    root.classList.remove('ew-open');
  };

  btn.addEventListener('pointerdown', start);
  btn.addEventListener('pointermove', move);
  btn.addEventListener('pointerup', end);
  btn.addEventListener('pointercancel', end);

  return {
    el: root,
    destroy(): void {
      root.remove();
    },
  };
}
